import type { FunctionComponent, ReactNode } from 'react';
import styled from 'styled-components';
import split from 'lodash/split';
import isString from 'lodash/isString';
import map from 'lodash/map';
import reduce from 'lodash/reduce';

const Container = styled.div`
	white-space: pre-line;
`;

const Paragraph = styled.p`
	line-height: 1.25;
	margin: 0;
	padding: 0.4em 0;
`;

const Highlight = styled.span`
	color: ${(props) => props.theme.colors.darkRed};
	font-weight: bolder;
`;

export interface Props {
	content: string[] | string;
}

const SPLIT_PATTERN = /\[[^\[\]]*\]/g;
const MATCH_PATTERN = /\[([^\[\]]*)\]/g;

const Text: FunctionComponent<Props> = (props: Props) => {
	const { content } = props;

	const textToNodes = (text: string): ReactNode => {
		const splitText = split(text, SPLIT_PATTERN);
		if (splitText.length <= 1) {
			return text;
		}

		const matches = Array.from(text.matchAll(MATCH_PATTERN));
		if (!matches) {
			return text;
		}

		const result = reduce<string, ReactNode[]>(
			splitText,
			(arr, element, index) => {
				return matches[index] && matches[index][1]
					? [
							...arr,
							element,
							<Highlight key={index}>{matches[index][1]}</Highlight>
					  ]
					: [...arr, element];
			},
			[]
		);
		return result;
	};

	const paragraphs = isString(content) ? [content] : content;
	const paragraphComponents = map(paragraphs, (text: string, index) => {
		return <Paragraph key={index}>{textToNodes(text)}</Paragraph>;
	});

	return <Container>{paragraphComponents}</Container>;
};

export default Text;
